/**
 * Theme packs. Picking a theme sets the album palette, the two content fonts and the cover/page
 * backgrounds — painted onto a new album by `createAlbumDocument` and onto every page added after —
 * and biases the sticker & background pickers towards matching tags. `plain` is the way to have
 * none of that: white pages, a white cover, ink and greys, for people who want only the photos.
 *
 * Every `headingFont` / `bodyFont` MUST be a family from @freealbum/text with supportsNikud === true —
 * the album's whole reason for existing is correctly pointed Hebrew. `themes.test.ts` enforces it.
 */
import type { AlbumTheme, Background } from '@freealbum/document';
import type { ThemePack } from './types';

const color = (c: string): Background => ({ type: 'color', color: c });
const gradient = (from: string, to: string, angleDeg = 180): Background => ({ type: 'gradient', from, to, angleDeg });
const asset = (assetId: string, mode: 'tile' | 'cover' = 'tile', opacity = 1): Background => ({
  type: 'asset',
  assetId,
  tint: null,
  opacity,
  mode,
});

export const THEMES: readonly ThemePack[] = [
  {
    id: 'family',
    nameKey: 'themes.family',
    // warm paper, ink, terracotta, amber, pine
    palette: ['#fbf8f3', '#22201d', '#c8553d', '#e0a030', '#2e5e4e'],
    headingFont: 'Frank Ruhl Libre',
    bodyFont: 'David Libre',
    coverBackground: gradient('#c8553d', '#8f3a29', 160),
    pageBackground: asset('paper-grain', 'tile', 0.5),
    stickerTags: ['family', 'everyday', 'celebration', 'doodle', 'photo'],
  },
  {
    id: 'wedding',
    nameKey: 'themes.wedding',
    // ivory, deep ink, dusty rose, champagne, sage
    palette: ['#fdfbf7', '#1f1b1a', '#c08a8a', '#cbb27c', '#8a9a86'],
    headingFont: 'Bellefair',
    bodyFont: 'Frank Ruhl Libre',
    coverBackground: color('#f4ece2'),
    pageBackground: asset('linen', 'tile', 0.45),
    stickerTags: ['wedding', 'floral', 'love', 'frame', 'ceremony'],
  },
  {
    id: 'travel',
    nameKey: 'themes.travel',
    // off-white, slate ink, sea blue, sand, sunset
    palette: ['#f7f6f3', '#1b2430', '#2f6d8f', '#d9b26a', '#d8623f'],
    headingFont: 'Secular One',
    bodyFont: 'Assistant',
    coverBackground: gradient('#2f6d8f', '#16354a', 200),
    pageBackground: asset('kraft', 'tile', 0.4),
    stickerTags: ['travel', 'journey', 'nature', 'summer', 'tape'],
  },
  {
    id: 'baby',
    nameKey: 'themes.baby',
    // milk, soft ink, blush, sky, mint
    palette: ['#fdfaf7', '#3a3540', '#eec1c6', '#b9d4e8', '#bfe0cd'],
    headingFont: 'Varela Round',
    bodyFont: 'Rubik',
    coverBackground: gradient('#eec1c6', '#b9d4e8', 150),
    pageBackground: asset('dots-fine', 'tile', 0.6),
    stickerTags: ['baby', 'playful', 'sky', 'celebration', 'nature'],
  },
  {
    id: 'plain',
    nameKey: 'themes.plain',
    // white, ink, and three greys: nothing that argues with the photographs
    palette: ['#ffffff', '#1b1b1f', '#55555b', '#9a9aa0', '#e6e6e9'],
    headingFont: 'Frank Ruhl Libre',
    bodyFont: 'David Libre',
    coverBackground: color('#ffffff'),
    pageBackground: color('#ffffff'),
    // No leaning: the sticker picker lists every tag in plain alphabetical order.
    stickerTags: [],
  },
];

const BY_ID = new Map(THEMES.map((t) => [t.id, t]));

export const DEFAULT_THEME_ID = 'family';

export function getTheme(id: string): ThemePack {
  return BY_ID.get(id) ?? BY_ID.get(DEFAULT_THEME_ID)!;
}

/**
 * The whole style as an `AlbumTheme`: what `createAlbumDocument` paints onto a new album and what an
 * `album.setTheme` command carries when switching. Copies, so no document shares the pack's objects.
 */
export function themeToAlbumTheme(id: string): AlbumTheme {
  const t = getTheme(id);
  return {
    id: t.id,
    palette: [...t.palette],
    headingFont: t.headingFont,
    bodyFont: t.bodyFont,
    coverBackground: { ...t.coverBackground },
    pageBackground: { ...t.pageBackground },
  };
}
